import React, { useState, useMemo } from "react";
import { OrderComparisonModal } from "./OrderComparisonModal";
import { useGlobalData, useGlobalOrders } from "../hooks/useGlobalHooks";
import { OptimizedNumberCell, OptimizedColorCell } from "./OptimizedCell";
import { useOrderStore } from "../contexts/LTPStore/useOrdersStore";
import apiClient from "../utils/apiClient";
import CancelConfirmationModal from "./CancelConfirmationModal";
import ExitSuccessModal from "./ExitSuccessModal";
import BulkOrderPlacementModal from "./BulkOrderPlacementModal";
import BulkModifyModal from "./BulkModifyModal";
import GroupSelectionModal from "./GroupSelectionModal";
import server from "../utils/serverConfig";
import { toast } from "react-hot-toast";

/**
 * Get broker badge info based on broker name
 * @param {string} broker - Broker name
 * @returns {Object} Badge info with text and color classes
 */
function getBrokerBadge(broker) {
  const brokerLower = broker?.toLowerCase() || "";

  if (brokerLower === "fyers") {
    return { text: "FY", class: "bg-blue-800 text-white" };
  } else if (brokerLower === "dhan_hq") {
    return { text: "DQ", class: "bg-emerald-950 text-white" };
  } else if (brokerLower.includes("dhan")) {
    return { text: "DH", class: "bg-emerald-800 text-white" };
  } else if (brokerLower === "nubra") {
    return { text: "NU", class: "bg-purple-800 text-white" };
  } else if (brokerLower === "zerodha") {
    return { text: "ZE", class: "bg-[#EF5350] text-white" };
  }

  return { text: "??", class: "bg-gray-700 text-white" };
}

/**
 * Get styling for client label badge
 * @param {string} label - Client label
 * @returns {string} Tailwind classes
 */
function getLabelStyles(label) {
  const l = label?.toLowerCase() || "";
  if (l.includes("clearmind")) {
    return "bg-purple-500/20 text-purple-400 border-purple-500/30";
  } else if (l.includes("og")) {
    return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  } else if (l.includes("satyam")) {
    return "bg-amber-500/20 text-amber-400 border-amber-500/30";
  }
  return "bg-blue-500/20 text-blue-400 border-blue-500/30";
}

/**
 * Get Tailwind color classes based on validation color
 * @param {string} color - 'green' or 'red'
 * @returns {string} Tailwind classes
 */
function getColorClasses(color) {
  switch (color) {
    case "green":
      return "bg-green-500/20 text-green-400";
    case "red":
      return "bg-red-500/20 text-red-400";
    default:
      return "";
  }
}

/**
 * Format number with commas
 * @param {number} num
 * @returns {string}
 */
function formatNumber(num) {
  if (num === undefined || num === null) return "-";
  return num.toLocaleString("en-IN");
}

/**
 * OrdersView Component
 * Displays client-wise buy orders grouped by index with color coding
 *
 * @param {Object} props
 * @param {Array} props.data - Processed orders data from useOrdersView hook
 * @param {Object} props.baseQtyMap - Base quantity map for each index
 * @param {Array} props.activeIndexes - List of active indexes to display
 * @param {Object} props.symbolStrategyBaseQty - Symbol+Strategy base qty map
 * @param {boolean} props.loading - Initial loading state (only true on first load)
 */
export function OrdersView({
  data,
  baseQtyMap,
  activeIndexes,
  allClientsActiveIndexes,
  symbolStrategyBaseQty,
  loading,
  clientData,
  allClientsData
}) {
  const { refetch } = useGlobalOrders();

  const { indexMultipliers, users } = useGlobalData();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAllClients, setShowAllClients] = useState(false);
  const orders = useOrderStore((state) => state.orders);

  // --- Cancel Modal State ---
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalActionType, setSuccessModalActionType] = useState("Exit");
  const [ordersToCancel, setOrdersToCancel] = useState([]);
  const [submittedOrders, setSubmittedOrders] = useState([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkModifyOpen, setBulkModifyOpen] = useState(false);
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [ordersToModify, setOrdersToModify] = useState([]);

  // Client Broker Map
  const clientBrokerMap = useMemo(() => {
    const map = {};
    if (clientData && Array.isArray(clientData)) {
      clientData.forEach(item => {
        map[item.clientId] = item.broker;
      });
    }
    return map;
  }, [clientData]);

  // Open Confirmation Modal
  const openCancelModal = (orders) => {
    setOrdersToCancel(orders);
    setConfirmModalOpen(true);
  };

  // Expose to window for the modal to use without prop drilling
  React.useEffect(() => {
    window.onCancelOrder = openCancelModal;
    return () => {
      delete window.onCancelOrder;
    };
  }, []);

  // Handle Confirmed Cancellation
  const handleConfirmCancel = async () => {
    if (!ordersToCancel.length) {
      setConfirmModalOpen(false);
      return;
    }

    try {
      setSuccessModalActionType("Cancel");
      const xtsOrders = [];
      const xtsAnuragOrders = [];
      const godseyeOrders = [];

      ordersToCancel.forEach(order => {
        const clientId = order.clientId || order.client_id;
        // Determine broker: Prioritize clientBrokerMap for reliable XTS variant routing
        let broker = "";
        if (clientBrokerMap && clientId) {
          broker = (clientBrokerMap[clientId] || "").toLowerCase();
        }
        if (!broker) {
          broker = (order.broker || "").toLowerCase();
        }
        if (!broker) broker = "fyers";

        const payloadOrder = {
          orderid: String(order.order_id || order.orderId),
          clientid: clientId,
          broker: broker,
        };

        if (broker === "dhan_xts" || broker === "xts" || broker === "dhan_ss") {
          xtsOrders.push(payloadOrder);
        } else if (broker === "dhan_xts_anurag" || broker === "dhan_ss_anurag") {
          xtsAnuragOrders.push(payloadOrder);
        } else {
          godseyeOrders.push(payloadOrder);
        }
      });
      // console.log("🚀 Canceling orders:", xtsOrders, xtsAnuragOrders, godseyeOrders);
      const promises = [];
      if (xtsOrders.length > 0) {
        promises.push(
          apiClient.post(`${server.ss_dhan.baseUrl}manual-cancel-orders-xts`, xtsOrders)
            .then(res => ({ type: 'xts', data: res.data }))
            .catch(err => ({ type: 'xts', error: err }))
        );
      }
      if (xtsAnuragOrders.length > 0) {
        promises.push(
          apiClient.post(`${server.ss_dhan_anurag.baseUrl}manual-cancel-orders-xts`, xtsAnuragOrders)
            .then(res => ({ type: 'xts_anurag', data: res.data }))
            .catch(err => ({ type: 'xts_anurag', error: err }))
        );
      }
      if (godseyeOrders.length > 0) {
        promises.push(
          apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, godseyeOrders)
            .then(res => ({ type: 'godseye', data: res.data }))
            .catch(err => ({ type: 'godseye', error: err }))
        );
      }

      const results = await Promise.all(promises);
      let allSubmittedOrders = [];
      let errorMsg = "";

      results.forEach(res => {
        if (res.error) {
          console.error(`❌ Error cancelling ${res.type} orders:`, res.error);
          errorMsg += `${res.type.toUpperCase()} failed: ${res.error.response?.data?.detail || res.error.message}. `;
        } else if (res.data && res.data.orders) {
          const responseOrders = res.data.orders.map(o => ({
            ...o,
            orderId: o.orderid,
            symbol: ordersToCancel[0]?.symbol
          }));
          allSubmittedOrders = [...allSubmittedOrders, ...responseOrders];
        }
      });

      if (allSubmittedOrders.length > 0) {
        setSubmittedOrders(allSubmittedOrders);
        setSuccessModalOpen(true);
      }

      if (errorMsg) {
        if (allSubmittedOrders.length === 0) {
          alert(`Failed to cancel orders: ${errorMsg}`);
        } else {
          console.warn("Partial cancellation error:", errorMsg);
        }
      }

      setConfirmModalOpen(false);

    } catch (error) {
      console.error("❌ Unexpected error cancelling orders:", error);
      alert(`Unexpected error: ${error.message}`);
      setConfirmModalOpen(false);
    }
  };

  const handleBulkSuccess = (orders) => {
    setSuccessModalActionType("Placement");
    setSubmittedOrders(orders);
    setSuccessModalOpen(true);
  };

  // Helper to remove order from list
  const handleRemoveFromBatch = (orderId) => {
    setOrdersToCancel(prev => {
      const newOrders = prev.filter(o => {
        const oid = o.order_id || o.orderId || o.appOrderID;
        return String(oid) !== String(orderId);
      });
      if (newOrders.length === 0) {
        setConfirmModalOpen(false); // Close if empty
      }
      return newOrders;
    });
  };

  // --- Compute Cancel Groups ---
  const cancelGroups = useMemo(() => {
    if (!orders || orders.length === 0) return { assets: {}, labels: {} };

    // 1. Filter strictly for Cancellable Buy Orders
    const cancellableOrders = orders.filter(o => {
      const oStatus = (o.status || "").toLowerCase();
      // Common pending statuses
      const isPending = oStatus === "pending" || oStatus === "open" || oStatus === "trigger pending" || oStatus === "not sent";

      const sideStr = (o.side ?? "").toString().trim().toUpperCase();
      const isBuy = sideStr === "1" || sideStr === "BUY";

      return isBuy && isPending;
    });

    const assets = {};
    const labels = {};

    // Create label map
    const labelMap = {};
    if (clientData) {
      clientData.forEach(c => labelMap[c.clientId] = c.label);
    }

    cancellableOrders.forEach(o => {
      const sym = (o.symbol || "").toUpperCase();
      const clientId = o.clientId || o.client_id;

      // Asset Grouping
      const knownAssets = ["NIFTY", "BANKNIFTY", "FINNIFTY", "SENSEX", "BANKEX", "MIDCPNIFTY"];
      let assetKey = null;
      for (const asset of knownAssets) {
        if (sym.includes(asset)) {
          // Check to avoid substring matches like NIFTY inside BANKNIFTY
          if (asset === "NIFTY" && (sym.includes("BANKNIFTY") || sym.includes("FINNIFTY") || sym.includes("MIDCPNIFTY"))) continue;
          assetKey = asset;
          break;
        }
      }
      if (assetKey) {
        if (!assets[assetKey]) assets[assetKey] = [];
        assets[assetKey].push(o);
      }

      // Label Grouping
      const label = labelMap[clientId];
      if (label) {
        if (!labels[label]) labels[label] = [];
        labels[label].push(o);
      }
    });

    return { assets, labels };
  }, [orders, clientData]);

  const handleOpenBulkModify = (orders) => {
    setOrdersToModify(orders);
    setBulkModifyOpen(true);
  };
  // Table filtering and sort state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");

  const handleOpenModal = (clientData) => {
    console.log("🎯 Opening order modal for client:", clientData);
    setSelectedClient(clientData);
    setModalOpen(true);
  };


  // Handle sort click
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg
          className="w-3 h-3 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    if (sortConfig.direction === "asc") {
      return (
        <svg
          className="w-3 h-3 text-blue-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 15l7-7 7 7"
          />
        </svg>
      );
    }

    return (
      <svg
        className="w-3 h-3 text-blue-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const completedOrders = orders.filter(order => Number(order.status_no) === 5);

  // console.log("📌 STATUS 5 ORDERS:", completedOrders);



  function extractStrike(symbol) {
    if (!symbol) return "";
    const m = symbol.match(/(\d{3,6})(CE|PE)$/);
    return m ? m[1] : "";
  }

  // === Helper: multiplier lookup for a given clientId + index ===
  // === Helper: multiplier lookup for a given clientId + index ===
  function getMultiplierForClient(clientId, index) {
    if (!indexMultipliers || !clientId || !index) return NaN;

    // Filter all matches for this client & index
    const matches = indexMultipliers.filter(
      (m) =>
        m.client_id === clientId &&
        String(m.index).toLowerCase() === String(index).toLowerCase()
    );

    // Prioritize 'dhan_xts' if it exists, otherwise use the first match
    const multObj =
      matches.find((m) => (m.broker || "").toLowerCase() === "dhan_xts") ||
      matches[0];

    return multObj ? Number(multObj.multiplier) : NaN;
  }

  // Check if a client row has mismatch
  const mismatchesByClientOrders = useMemo(() => {
    if (!orders || orders.length === 0 || !symbolStrategyBaseQty) return {};

    const map = {};

    // Group orders by client
    const ordersByClient = orders.reduce((acc, o) => {
      const clientId = o.clientId || o.client_id || "UNKNOWN";
      if (!acc[clientId]) acc[clientId] = [];
      acc[clientId].push(o);
      return acc;
    }, {});

    Object.entries(ordersByClient).forEach(([clientId, clientOrders]) => {
      const processedKeys = new Set();

      clientOrders.forEach((order) => {

        const sideStr = (order.side ?? "").toString().trim().toUpperCase();
        const isBuy = sideStr === "1" || sideStr === "BUY";
        if (!isBuy) return;

        const key = `${order.symbol}_${order.strategy || "NO_STRATEGY"}`;
        if (processedKeys.has(key)) return;
        processedKeys.add(key);

        const baseQtyInfo = symbolStrategyBaseQty[key];
        if (!baseQtyInfo) return; // skip if no base info

        const index = baseQtyInfo.index?.toUpperCase();
        const multiplier = getMultiplierForClient(clientId, index);
        const baseQty = Number(baseQtyInfo.baseQty || 0);
        const expectedQty = Number(multiplier) * baseQty;

        // Sum actual qty for this symbol+strategy
        let actualQty = 0;
        clientOrders.forEach((o) => {
          const oKey = `${o.symbol}_${o.strategy || "NO_STRATEGY"}`;
          if (oKey === key) {
            if (o.side === 1) {
              actualQty += Math.abs(Number(o.qty || 0));
            }

          }
        });

        const reasons = [];
        if (!order.strategy || order.strategy === "N/A") reasons.push("strategy missing / N/A");
        if (!Number.isFinite(multiplier)) reasons.push("multiplier missing for client/index");
        if (!Number.isFinite(expectedQty)) reasons.push("expectedQty could not be calculated");
        if (Number.isFinite(expectedQty) && expectedQty !== Number(actualQty))
          reasons.push(`qty mismatch (expected ${expectedQty}, got ${actualQty})`);
        if (actualQty === 0 && Number.isFinite(expectedQty) && expectedQty > 0)
          reasons.push("actual qty is 0 but expected > 0");

        if (reasons.length > 0) {
          if (!map[clientId]) map[clientId] = [];
          map[clientId].push({
            symbol: order.symbol,
            strategy: order.strategy || "N/A",
            strike: extractStrike(order.symbol),
            multiplier: Number.isFinite(multiplier) ? multiplier : null,
            expectedQty: Number.isFinite(expectedQty) ? expectedQty : null,
            actualQty,
            reasons,
          });
        }
      });
    });

    return map;
  }, [orders, symbolStrategyBaseQty]);


  // Use mode-specific indexes: Normal shows only indexes with actual orders, All Clients shows all asset columns
  const effectiveIndexes = showAllClients
    ? (allClientsActiveIndexes || activeIndexes)
    : activeIndexes;

  // Process data with sorting (keeping existing red validation priority)
  const processedData = useMemo(() => {
    const dataSource = showAllClients ? (allClientsData || []) : (data || []);
    if (!dataSource || dataSource.length === 0) return [];

    let processed = dataSource.map(row => {
      const user = users?.find(u =>
        (u.clientId || u.client_id || "").toString().trim() === row.clientId.toString().trim()
      );
      return {
        ...row,
        label: user?.label || ""
      };
    });

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();

      processed = processed.filter((row) => {
        const clientId = row.clientId?.toLowerCase() || "";
        const clientName = row.clientName?.toLowerCase() || "";
        const broker = row.broker?.toLowerCase() || "";
        const symbol = row.symbol?.toLowerCase() || "";
        const orderId = String(row.orderId || "").toLowerCase();
        const label = row.label?.toLowerCase() || "";

        return (
          clientId.includes(q) ||
          clientName.includes(q) ||
          broker.includes(q) ||
          symbol.includes(q) ||
          orderId.includes(q) ||
          label.includes(q)
        );
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      processed = processed.sort((a, b) => {
        let aValue, bValue;

        // Handle nested index data (e.g., "NIFTY_multiplier", "BANKNIFTY_qty")
        if (sortConfig.key.includes("_")) {
          const [indexName, property] = sortConfig.key.split("_");
          aValue =
            a[indexName]?.[property] ||
            a[indexName]?.[
            property === "multiplier"
              ? "multiplier"
              : property === "qty"
                ? "qty"
                : 0
            ];
          bValue =
            b[indexName]?.[property] ||
            b[indexName]?.[
            property === "multiplier"
              ? "multiplier"
              : property === "qty"
                ? "qty"
                : 0
            ];
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Compare values
        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        // String comparison
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    // Sort data to show red validation errors at the top (existing logic)
    processed.sort((a, b) => {
      // 🔥 Priority 1: mismatch clients
      const aHasMismatch = !!mismatchesByClientOrders?.[a.clientId]?.length;
      const bHasMismatch = !!mismatchesByClientOrders?.[b.clientId]?.length;

      if (aHasMismatch && !bHasMismatch) return -1;
      if (!aHasMismatch && bHasMismatch) return 1;

      // 🔴 Priority 2: red validation
      const aHasRed =
        effectiveIndexes?.some((index) => a[index]?.color === "red") || false;
      const bHasRed =
        effectiveIndexes?.some((index) => b[index]?.color === "red") || false;

      if (aHasRed && !bHasRed) return -1;
      if (!aHasRed && bHasRed) return 1;

      return 0;
    });

    return processed;
  }, [data, allClientsData, showAllClients, sortConfig, activeIndexes, mismatchesByClientOrders, searchQuery, effectiveIndexes]);

  // Per-index coverage: how many active clients have multiplier (total) and how many got orders (placed)

  const coverageMap = useMemo(() => {
    const map = {};
    if (!effectiveIndexes || !allClientsData) return map;
    effectiveIndexes.forEach((idx) => {
      const total = allClientsData.filter((row) => row[idx] !== undefined).length;
      const placed = allClientsData.filter((row) => row[idx]?.hasOrders === true).length;
      map[idx] = { placed, total };
    });
    return map;
  }, [allClientsData, effectiveIndexes]);



  //console.log("MISMATCHED_-------",mismatchesByClientOrders);
  // Show loading state (only on initial load)
  if (loading) {
    return (
      <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg
            className="inline-block animate-spin h-12 w-12 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-400 mt-4">Loading orders...</p>
        </div>
      </div>
    );
  }


  return (
    <>
      {/* Custom Scrollbar Styles */}
      <style>{`
        .sleek-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .sleek-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.3);
          border-radius: 10px;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.5);
        }
      `}</style>
      <div className="flex flex-col h-full">
        {/* Base Qty Info and Legend in same row */}
        <div className="flex justify-between items-center p-3 mb-3 bg-[#14161f] border-b border-gray-800/50 flex-wrap gap-4">
          {/* Left Side: Info & Legend */}
          <div className="flex items-center gap-6 flex-wrap">
            {/* Base Qty Info */}
            {baseQtyMap && Object.keys(baseQtyMap).length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider">
                  Base Qty:
                </span>
                <div className="flex items-center gap-2">
                  {Object.entries(baseQtyMap).map(([index, qty]) => {
                    const idxLower = index.toLowerCase();
                    const coverage = coverageMap[idxLower];
                    const placed = coverage?.placed ?? 0;
                    const total = coverage?.total ?? 0;
                    const allPlaced = total > 0 && placed === total;
                    const nonePlaced = total > 0 && placed === 0;
                    return (
                      <span
                        key={index}
                        className="bg-[#0f1116] border border-gray-800 px-2.5 py-1 rounded-md text-xs text-gray-300 shadow-inner flex items-center gap-1.5"
                      >
                        <span className="font-bold text-blue-400">{index}:</span>
                        <span>{formatNumber(qty)}</span>
                        {total > 0 && (
                          <span
                            className={`ml-1 font-bold px-1.5 py-0.5 rounded text-[10px] border ${
                              allPlaced
                                ? "bg-green-500/15 text-green-400 border-green-500/30"
                                : nonePlaced
                                ? "bg-red-500/15 text-red-400 border-red-500/30"
                                : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                            }`}
                            title={`${placed} of ${total} clients have orders placed`}
                          >
                            {placed}/{total}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Fallback: show coverage badges even when no base qty (no buy orders yet) */}
            {(!baseQtyMap || Object.keys(baseQtyMap).length === 0) && Object.keys(coverageMap).length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-gray-400 font-bold text-[11px] uppercase tracking-wider">Coverage:</span>
                <div className="flex items-center gap-2">
                  {Object.entries(coverageMap).map(([idx, { placed, total }]) => {
                    const allPlaced = total > 0 && placed === total;
                    const nonePlaced = total > 0 && placed === 0;
                    return (
                      <span key={idx} className="bg-[#0f1116] border border-gray-800 px-2.5 py-1 rounded-md text-xs text-gray-300 shadow-inner flex items-center gap-1.5">
                        <span className="font-bold text-blue-400">{idx.toUpperCase()}:</span>
                        <span
                          className={`font-bold px-1.5 py-0.5 rounded text-[10px] border ${
                            allPlaced
                              ? "bg-green-500/15 text-green-400 border-green-500/30"
                              : nonePlaced
                              ? "bg-red-500/15 text-red-400 border-red-500/30"
                              : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                          }`}
                          title={`${placed} of ${total} clients have orders placed`}
                        >
                          {placed}/{total}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {(baseQtyMap && Object.keys(baseQtyMap).length > 0) && (
              <div className="w-px h-6 bg-gray-700/50 hidden md:block"></div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-4 text-[10px] uppercase font-bold tracking-tight">
              <div className="flex items-center gap-2 group">
                <span className="w-3 h-3 bg-green-500/20 border border-green-500/50 rounded-sm shadow-[0_0_8px_rgba(34,197,94,0.2)]"></span>
                <span className="text-gray-400 group-hover:text-green-400 transition-colors">All OK</span>
              </div>
              <div className="flex items-center gap-2 group">
                <span className="w-3 h-3 bg-red-500/20 border border-red-500/50 rounded-sm shadow-[0_0_8px_rgba(239,68,68,0.2)]"></span>
                <span className="text-gray-400 group-hover:text-red-400 transition-colors">Issue</span>
              </div>
            </div>
          </div>

          {/* Right Side: Actions */}
          <div className="flex items-center gap-4 flex-wrap ml-auto">

            {/* Cancel Buttons Section */}
            {(Object.keys(cancelGroups.assets).length > 0 || Object.keys(cancelGroups.labels).length > 0) && (
              <div className="flex items-center gap-2 pr-2">
                {/* Asset Wise */}
                {Object.entries(cancelGroups.assets).map(([asset, assetOrders]) => (
                  <button
                    key={asset}
                    onClick={() => openCancelModal(assetOrders)}
                    className="px-3 py-1.5 bg-red-900/20 hover:bg-red-800/40 text-red-400 text-[10px] font-bold rounded border border-red-800/30 transition-all flex items-center gap-2 uppercase tracking-wide hover:shadow-lg hover:shadow-red-900/20"
                  >
                    <span>Cancel {asset}</span>
                    <span className="bg-red-950/50 px-1.5 rounded text-[10px]">{assetOrders.length}</span>
                  </button>
                ))}

                {/* Label Wise */}
                {Object.entries(cancelGroups.labels).map(([label, labelOrders]) => (
                  <button
                    key={label}
                    onClick={() => openCancelModal(labelOrders)}
                    className="px-3 py-1.5 bg-orange-900/20 hover:bg-orange-800/40 text-orange-400 text-[10px] font-bold rounded border border-orange-800/30 transition-all flex items-center gap-2 uppercase tracking-wide hover:shadow-lg hover:shadow-orange-900/20"
                  >
                    <span>CANCEL {label}</span>
                    <span className="bg-orange-950/50 px-1.5 rounded text-[10px]">{labelOrders.length}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Divider between cleanup and primary actions */}
            {(Object.keys(cancelGroups.assets).length > 0 || Object.keys(cancelGroups.labels).length > 0) && (
              <div className="w-px h-6 bg-gray-700/50 hidden sm:block"></div>
            )}

            <div className="flex items-center gap-3">
              {/* Toggle All Clients */}
              <div className="flex items-center bg-[#0f1116] rounded-md p-1 border border-gray-800">
                <button
                  onClick={() => setShowAllClients(false)}
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${
                    !showAllClients
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-400 hover:text-gray-200 hover:bg-[#1a1d29]"
                  }`}
                >
                  Normal
                </button>
                <button
                  onClick={() => setShowAllClients(true)}
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${
                    showAllClients
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-400 hover:text-gray-200 hover:bg-[#1a1d29]"
                  }`}
                >
                  All Clients
                </button>
              </div>

              {/* Bulk Modify Button (Unified) */}
              <button
                onClick={() => setIsSelectionModalOpen(true)}
                className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-indigo-900/40 transition-all flex items-center gap-2 border border-indigo-400/30"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>Bulk Entry Order Modify</span>
              </button>

              {/* Bulk Order Button */}
              <button
                onClick={() => setBulkModalOpen(true)}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-black uppercase tracking-wider rounded shadow-lg shadow-blue-900/40 transition-all flex items-center gap-2 border border-blue-400/30"
              >
                <span>Order Placement</span>
              </button>
            </div>
          </div>
        </div>

        {(!processedData || processedData.length === 0) && !searchQuery ? (
          <div className="flex-1 flex flex-col items-center justify-center p-12 bg-[#1a1d29]/50">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-500/20">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-white text-lg font-bold mb-2">No Order Data</h3>
              <p className="text-gray-400 text-sm mb-6">
                No orders found for the current selection. Use the button above to place new orders.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="relative">
              <input
                type="text"
                placeholder="Search broker / client id / name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="
        bg-[#0f1116]
        border border-gray-700
        text-gray-200
        text-sm
        px-3 py-1.5
        rounded
        w-64
        ml-6
        focus:outline-none
        focus:ring-1
        focus:ring-blue-500
        placeholder-gray-500
      "
              />

            </div>
            <div className="flex-1 overflow-auto sleek-scrollbar">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-[#14161f] sticky top-0 z-[2]">
                  <tr className="">

                    {/* No. Header */}
                    <th
                      rowSpan="2"
                      className="text-center py-2 text-gray-400 font-medium bg-[#14161f] z-20 border-r border-gray-700/50"
                    >
                      No.
                    </th>

                    {/* Name Header */}
                    <th
                      rowSpan="2"
                      className="text-left py-2 px-2 text-gray-300 font-medium bg-[#14161f] z-20 border-r border-gray-700/50 w-48 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                      onClick={() => handleSort("clientName")}
                    >
                      <div className="flex items-center gap-1">
                        <span>Name</span>
                        {getSortIcon("clientName")}
                      </div>
                    </th>

                    {effectiveIndexes &&
                      effectiveIndexes.map((index) => (
                        <th
                          key={index}
                          colSpan="2"
                          className="text-center py-2 px-2 text-white font-semibold bg-[#14161f] z-10 border-l border-gray-700/50 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                          onClick={() => handleSort(index)}
                        >
                          <div className="flex items-center justify-center gap-1">
                            <span>{index.toUpperCase()}</span>
                            {getSortIcon(index)}
                          </div>
                        </th>
                      ))}
                  </tr>
                  <tr className="border-b border-gray-700/50">
                    {effectiveIndexes &&
                      effectiveIndexes.map((index) => (
                        <React.Fragment key={index}>
                          <th
                            className="text-center py-1 px-1 text-gray-400 text-xs font-medium bg-[#14161f] z-10 border-l border-gray-700/50 w-12 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                            onClick={() => handleSort(`${index}_multiplier`)}
                          >
                            <div className="flex items-center justify-center gap-1">
                              <span>Mul</span>
                              {getSortIcon(`${index}_multiplier`)}
                            </div>
                          </th>
                          <th
                            className="text-center py-1 px-1 text-gray-400 text-xs font-medium bg-[#14161f] z-10 border-l border-gray-700/30 w-20 cursor-pointer hover:bg-[#1a1d29] transition-colors"
                            onClick={() => handleSort(`${index}_qty`)}
                          >
                            <div className="flex items-center justify-center gap-1">
                              <span>QTY</span>
                              {getSortIcon(`${index}_qty`)}
                            </div>
                          </th>
                        </React.Fragment>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {processedData.map((row, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-800/50 hover:bg-[#14161f] transition-colors"
                    >
                      <td className="py-1 text-center text-gray-500 bg-[#1a1d29] border-r border-gray-700/50 w-3">
                        {idx + 1}
                      </td>

                      <td className="py-1.5 px-2 bg-[#1a1d29] z-10 border-r border-gray-700/50 w-48 overflow-visible">
                        <div className="flex items-center gap-2">
                          <div className="text-white font-medium flex items-center gap-2">
                            <span
                              className={`px-1.5 py-0.5 rounded text-xs font-bold ${getBrokerBadge(row.broker).class
                                }`}
                            >
                              {getBrokerBadge(row.broker).text}
                            </span>

                            <span>
                              {row.clientName}{" "}
                              {row.label && (
                                <span
                                  className={`text-[10px] px-1 rounded font-bold border ml-1 ${getLabelStyles(row.label)}`}
                                  title={row.label}
                                >
                                  {row.label.charAt(0).toUpperCase()}
                                </span>
                              )}
                              <span className="text-xs text-gray-500 ml-1">
                                ({row.clientId})
                              </span>
                            </span>
                          </div>

                          {/* RIGHT SIDE ACTIONS */}
                          <div className="flex items-center gap-2 ml-auto shrink-0">
                            {/* ALERT ICON */}
                            {mismatchesByClientOrders[row.clientId] &&
                              mismatchesByClientOrders[row.clientId].length > 0 && (
                                <div className="relative inline-block group">
                                  <span className="bg-red-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full cursor-pointer select-none">
                                    !
                                  </span>

                                  <div
                                    className="
                absolute left-full top-1/2 -translate-y-1/2 ml-2
                hidden group-hover:block
                bg-gray-900 text-gray-100 text-sm p-3 rounded-lg shadow-xl
                w-80 max-h-96 overflow-y-auto
                border border-gray-700
                z-30
                transition-all duration-200
              "
                                  >
                                    <div className="font-semibold text-red-400 mb-2 text-sm flex items-center justify-between">
                                      <span>Mismatch Details</span>
                                      <span className="text-gray-400 text-xs">
                                        {mismatchesByClientOrders[row.clientId].length} issue
                                        {mismatchesByClientOrders[row.clientId].length > 1 ? "s" : ""}
                                      </span>
                                    </div>

                                    {mismatchesByClientOrders[row.clientId].map((issue, i) => (
                                      <div
                                        key={i}
                                        className="mb-3 p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                                      >
                                        <div className="text-blue-400 font-medium text-sm mb-1">
                                          {issue.symbol} ({issue.strategy})
                                        </div>
                                        <ul className="list-disc list-inside text-gray-300 text-xs space-y-0.5">
                                          {issue.reasons.map((r, j) => (
                                            <li key={j} className="leading-snug">
                                              {r}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                            {/* COMPARE BUTTON */}
                            <button
                              onClick={() => handleOpenModal(row)}
                              className="text-blue-400 hover:text-blue-300 transition-colors"
                              title="Compare orders"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </td>


                      {effectiveIndexes &&
                        effectiveIndexes.map((index) => {
                          const indexData = row[index];

                          if (!indexData) {
                            return (
                              <React.Fragment key={index}>
                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/50">
                                  -
                                </td>
                                <td className="py-1.5 px-1 text-center text-gray-600 border-l border-gray-700/30">
                                  -
                                </td>
                              </React.Fragment>
                            );
                          }

                          const colorClasses = getColorClasses(indexData.color);

                          return (
                            <React.Fragment key={index}>
                              <td className="py-1.5 px-1 text-center text-gray-300 border-l border-gray-700/50">
                                <OptimizedNumberCell
                                  value={indexData.multiplier}
                                  className="text-center text-gray-300"
                                />
                              </td>
                              <td
                                className="py-1.5 px-1 text-center font-medium border-l border-gray-700/30"
                                title={`Expected: ${formatNumber(
                                  indexData.expectedQty
                                )}${!indexData.hasOrders ? " | No orders placed" : ""
                                  }`}
                              >
                                <OptimizedColorCell
                                  value={
                                    indexData.hasOrders === false
                                      ? null
                                      : indexData.qty
                                  }
                                  color={indexData.color}
                                  expectedValue={indexData.expectedQty}
                                  formatFn={(val) =>
                                    val === null ? "-" : formatNumber(val)
                                  }
                                  className="text-center font-medium"
                                  applyColorToCell={true}
                                />
                              </td>
                            </React.Fragment>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Order Comparison Modal */}
            <OrderComparisonModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              clientData={selectedClient}
              orders={orders}
              symbolStrategyBaseQty={symbolStrategyBaseQty}
            />

            {/* Cancel Confirmation Modal */}
            <CancelConfirmationModal
              isOpen={confirmModalOpen}
              onClose={() => setConfirmModalOpen(false)}
              onConfirm={handleConfirmCancel}
              orders={ordersToCancel}
              onDelete={handleRemoveFromBatch}
              isEscalation={false}
              title="Cancel Buy Orders"
              message={`Are you sure you want to cancel ${ordersToCancel.length} buy order(s)?`}
            />
          </>
        )}

        {/* Success Modal */}
        <ExitSuccessModal
          isOpen={successModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          orders={submittedOrders}
          actionType={successModalActionType}
        />

        <BulkOrderPlacementModal
          isOpen={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          users={users}
          indexMultipliers={indexMultipliers}
          onSuccess={handleBulkSuccess}
          existingOrders={orders}
        />

        <BulkModifyModal
          isOpen={bulkModifyOpen}
          onClose={() => setBulkModifyOpen(false)}
          orders={ordersToModify}
          onSuccess={() => refetch()}
        />

        <GroupSelectionModal
          isOpen={isSelectionModalOpen}
          onClose={() => setIsSelectionModalOpen(false)}
          orders={orders}
          onConfirm={handleOpenBulkModify}
          assetFilterType="options"
        />
      </div>
    </>
  );
}
